import { Fragment, useEffect, useState, type ComponentProps, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { ApiError, isTransient, messageFor, NETWORK_ERROR } from "@shared/client/api";
import { t as messages, useT } from "@shared/client/locale";
import { Link } from "@shared/client/navigation";
import { useSessionError } from "@shared/client/session";
import { Button } from "@shared/components/ui/button";
import { Skeleton } from "@shared/components/ui/skeleton";
import { LEGAL_ENTITY } from "@shared/lib/legal";
import { cn } from "@shared/lib/utils";

/**
 * Les états d'un écran qui attend ses données (charte §6.5) : le squelette de
 * sa structure tant qu'elles arrivent, une erreur qui dit la cause et propose
 * la reprise si elles n'arrivent pas. Jamais d'écran blanc, jamais de trace
 * technique à l'écran.
 *
 * Usage, dans une page qui lit sa vue :
 *   const { data, error } = useView<…>("/api/views/…");
 *   if (!data) return <ViewFallback error={error} skeleton={<…Skeleton />} subject="vos leads" />;
 */

/** Sous ce délai, la réponse arrive avant que l'attente ne se remarque : rien ne s'affiche. */
const SKELETON_DELAY_MS = 180;

/** Vrai une fois `ms` écoulées depuis le montage. */
export function useDelayed(ms = SKELETON_DELAY_MS) {
  const [elapsed, setElapsed] = useState(false);
  useEffect(() => {
    const timer = window.setTimeout(() => setElapsed(true), ms);
    return () => window.clearTimeout(timer);
  }, [ms]);
  return elapsed;
}

/**
 * Un bloc de squelette : gris et immobile. La pulsation reste le signe d'un
 * passage de l'agent en cours (charte §2.6), pas d'un écran qui se charge.
 */
export function Placeholder({ className }: { className?: string }) {
  return <Skeleton className={cn("animate-none", className)} />;
}

/** Le squelette d'un écran : annoncé aux lecteurs d'écran, montré après un court délai. */
function Delayed({ label, children }: { label: string; children: ReactNode }) {
  const visible = useDelayed();
  return (
    <div aria-busy="true" role="status">
      <span className="sr-only">{label}</span>
      {visible && <div aria-hidden="true">{children}</div>}
    </div>
  );
}

/**
 * Où s'affiche un état : `page`, une page autonome (titre en serif) ;
 * `content`, à la place d'un écran dans la coque ; `section`, à la place d'une
 * section d'écran (l'abonnement dans les paramètres), sans prendre le titre
 * de la page.
 */
export type StatePlacement = "page" | "content" | "section";

/** Un état à la place du contenu : ce qui se passe, puis ce qu'on peut faire. */
export function StateMessage({
  title,
  children,
  actions,
  placement = "content",
  alert = false,
}: {
  title: string;
  children: ReactNode;
  actions?: ReactNode;
  placement?: StatePlacement;
  alert?: boolean;
}) {
  const Heading = placement === "section" ? "h3" : "h1";
  return (
    <section
      className={cn(
        "grid gap-2.5",
        placement === "section"
          ? "justify-items-start rounded-lg border p-5 max-sm:p-4"
          : "mx-auto max-w-[480px] justify-items-center text-center",
        placement === "content" && "py-16 max-md:py-10",
      )}
      role={alert ? "alert" : undefined}
    >
      <Heading
        className={cn(
          "m-0",
          placement === "page" && "font-serif text-[32px] leading-tight font-normal tracking-tight",
          placement === "content" && "text-[17px] font-semibold tracking-[-0.01em]",
          placement === "section" && "text-[14px] font-semibold",
        )}
      >
        {title}
      </Heading>
      <div className="grid gap-2 text-[13.5px] leading-relaxed text-muted-foreground [&_p]:m-0">{children}</div>
      {actions && (
        <div className={cn("mt-3 flex flex-wrap items-center gap-2.5", placement !== "section" && "justify-center")}>
          {actions}
        </div>
      )}
    </section>
  );
}

/** « Écrivez-nous » : l'adresse de contact des pages légales. */
export function ContactLine() {
  const t = useT();
  const parts = t.common.states.contact(
    <a className="text-primary underline-offset-2 hover:underline" href={`mailto:${LEGAL_ENTITY.email}`}>
      {LEGAL_ENTITY.email}
    </a>,
  );
  return (
    <p>
      {parts.map((part, index) => (
        <Fragment key={index}>{part}</Fragment>
      ))}
    </p>
  );
}

export type NotFoundCopy = { title: string; body: string; href: string; label: string };

/** Le 404 dans la coque : rail et topbar restent, le chemin du retour aussi. */
export function NotFoundState({ copy: given, placement }: { copy?: NotFoundCopy; placement?: StatePlacement }) {
  const t = useT();
  const copy = given ?? { ...t.common.states.notFound, href: "/app", label: t.common.states.overview };
  return (
    <StateMessage
      actions={
        <Button asChild variant="outline">
          <Link href={copy.href}>{copy.label}</Link>
        </Button>
      }
      placement={placement}
      title={copy.title}
    >
      <p>{copy.body}</p>
    </StateMessage>
  );
}

/** La vue répond que l'objet demandé n'existe pas (ou pas pour ce compte). */
export function isNotFound(error: unknown) {
  return error instanceof ApiError && error.status === 404;
}

/**
 * La cause, en français et sans jargon, d'un chargement qui a échoué : pour
 * une coupure ou une panne, le libellé d'apiFetch (jamais le détail technique
 * d'un 5xx) ; sinon, le message de l'API.
 */
function causeOf(error: unknown) {
  if (!(error instanceof ApiError)) return messages().common.states.loadInterrupted;
  if (error.status === NETWORK_ERROR || error.status >= 500) return messageFor(error.status);
  return error.message;
}

/**
 * Relance tout ce qui a échoué à l'écran : la vue de la page, et la coque si
 * elle aussi manque. Le squelette reprend aussitôt la place de l'erreur.
 */
export function RetryButton({ className, size, variant }: Pick<ComponentProps<typeof Button>, "className" | "size" | "variant">) {
  const client = useQueryClient();
  const t = useT();
  return (
    <Button
      className={className}
      onClick={() => void client.refetchQueries({ type: "active", predicate: (query) => query.state.status === "error" })}
      size={size}
      type="button"
      variant={variant}
    >
      {t.common.states.retry}
    </Button>
  );
}

/**
 * Une vue qui n'a pas pu être lue. Un 401 ne s'affiche pas : la session passe
 * à `null` (providers.tsx) et la garde renvoie vers la connexion, retour
 * compris. Un 404 dit « introuvable » et ramène en terrain connu. Une coupure
 * ou une panne dit la cause et propose de réessayer ; un refus (403, autre
 * 4xx) ne changera pas en insistant : il ramène à la Vue d'ensemble.
 */
type ViewErrorProps = {
  error: unknown;
  /** Ce qui manque, après « Impossible de charger » : « vos leads », « ce rapport »… */
  subject: string;
  notFound?: NotFoundCopy;
  placement?: StatePlacement;
};

export function ViewError({ error, subject, notFound, placement }: ViewErrorProps) {
  const t = useT();
  const copy = t.common.states;
  if (error instanceof ApiError && error.status === 401) return null;
  if (isNotFound(error)) return <NotFoundState copy={notFound} placement={placement} />;
  const transient = isTransient(error);
  let actions: ReactNode = null;
  if (transient) actions = <RetryButton variant={placement === "section" ? "outline" : "default"} />;
  else if (placement !== "section") {
    actions = (
      <Button asChild variant="outline">
        <Link href="/app">{copy.overview}</Link>
      </Button>
    );
  }
  return (
    <StateMessage actions={actions} alert placement={placement} title={copy.loadFailed(subject)}>
      <p>
        {causeOf(error)}
        {transient && ` ${copy.nothingLost}`}
      </p>
    </StateMessage>
  );
}

/**
 * Ce qu'une page rend tant que sa vue n'a pas de données : son squelette,
 * ou l'erreur qui l'en prive. Une vue déjà en cache s'affiche directement,
 * sans repasser par ici.
 */
export function ViewFallback({ error, skeleton, ...state }: ViewErrorProps & { skeleton: ReactNode }) {
  const t = useT();
  if (error) return <ViewError error={error} {...state} />;
  return <Delayed label={t.common.states.loading(state.subject)}>{skeleton}</Delayed>;
}

/** Une page à part, hors de la coque et des pages d'auth : le logo, puis l'état, au centre. */
export function StandaloneFrame({ children }: { children: ReactNode }) {
  return (
    <main className="grid min-h-screen place-items-center bg-background px-4 py-12">
      <div className="grid justify-items-center gap-8">
        <img alt="Hune" className="size-[30px] object-contain" height={30} src="/brand/Logo.svg" width={30} />
        {children}
      </div>
    </main>
  );
}

/**
 * Ce que rend une garde hors de la coque (auth, onboarding) tant que la
 * session est inconnue : rien pendant la lecture, brève, et la page suit ;
 * l'erreur et sa reprise si la session n'a pas pu être lue (API qui
 * redémarre, réseau coupé). `standalone` : sur une page à part (l'onboarding
 * n'a pas de cadre) ; sinon dans le cadre des pages d'auth.
 */
export function SessionFallback({ standalone = false }: { standalone?: boolean }) {
  const error = useSessionError();
  const t = useT();
  if (!error) return null;
  const state = <ViewError error={error} placement="page" subject={t.common.states.thisPage} />;
  return standalone ? <StandaloneFrame>{state}</StandaloneFrame> : state;
}
